import i18next from "i18next";

/**
 * Cartão de vaga da vitrine do tema Employer Page, em HTML.
 *
 * Existe como módulo porque DUAS entradas do candidato precisam do mesmo cartão: o "carregar mais"
 * da vitrine (hotsite-employer-page.js) e o modo "todas as vagas com busca e filtro" (vagas.js) —
 * e as duas convivem na mesma página quando a home está em "todas".
 *
 * ⚠️ A REFERÊNCIA é `Views/Shared/HotSites/EmployerPage/_CartaoVagaVitrine.cshtml`, que é quem
 * renderiza a primeira carga (a que o buscador lê). Mudou lá, muda aqui. Sobra ainda uma terceira
 * reprodução, no `pagina-carreiras-previa.js` do **Corporate** — outro projeto, outro webpack, fora
 * do alcance deste módulo; ela desenha o tema dentro do iframe da prévia do editor.
 *
 * As chaves de tradução usadas aqui precisam existir no pacote i18next de CADA entrada que importar
 * este módulo (`localizacao/vagas/*` e `localizacao/hotsite-employer-page/*`).
 */

// Só as modalidades que ACRESCENTAM. "Presencial" repete o que a cidade já diz, e "Totalmente
// Remoto" é o próprio texto da linha de local — nessa modalidade o VagaBusiness põe a modalidade no
// LUGAR da cidade (VagaBusiness.cs:2269). Testa o nome do enum, nunca o texto: `trabalhoRemotoTexto`
// vem traduzido e a comparação quebraria em en-us/es-cl.
const MODALIDADES_QUE_ACRESCENTAM = ["RemotoFlexivel", "TemporariamenteRemoto"];

export function escaparHtml(valor) {
    return String(valor == null ? "" : valor)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
}

/**
 * Rótulo de recência a partir do `timestamp` (Unix de DataAtualizacao).
 * Mesma conta do servidor: diferença em DIAS DE CALENDÁRIO, não em horas corridas — por horas uma
 * vaga de ontem às 23h sairia como "Hoje".
 */
export function textoRecencia(timestamp) {
    if (!timestamp || timestamp <= 0) return "";

    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);

    const atualizacao = new Date(timestamp * 1000);
    atualizacao.setHours(0, 0, 0, 0);

    const dias = Math.max(0, Math.round((hoje - atualizacao) / 86400000));

    if (dias === 0) return i18next.t("VagaVitrineHoje");
    if (dias === 1) return i18next.t("VagaVitrineOntem");
    if (dias <= 30) return i18next.t("VagaVitrineHaDias", { dias: dias });
    return i18next.t("VagaVitrineHaMaisDeUmMes");
}

/**
 * @param urlBase Endereço da página de trabalhe conosco quando o cartão roda FORA dela (a vitrine embutida
 *   no site do cliente, vitrine-vagas.js): o `url` da vaga é relativo e o link abre em nova aba.
 */
export function cartaoVagaVitrine(vaga, nivelTitulo = "h3", urlBase = "") {
    const chips = [];
    // O nível saiu do cartão (ver _CartaoVagaVitrine.cshtml): chip em toda vaga, sem ajudar a escolher.
    if (MODALIDADES_QUE_ACRESCENTAM.includes(vaga.trabalhoRemoto) && vaga.trabalhoRemotoTexto) {
        chips.push(vaga.trabalhoRemotoTexto);
    }

    const cidades = Array.isArray(vaga.cidades) ? vaga.cidades.join(" · ") : "";
    const salario = vaga.salario || "";
    const quando = textoRecencia(vaga.timestamp);

    const chipsHtml = (chips.length || vaga.pcd)
        ? `<ul class="cartao-vaga-chips">${chips.map(c => `<li>${escaparHtml(c)}</li>`).join("")}`
          + `${vaga.pcd ? `<li class="chip-pcd">${escaparHtml(i18next.t("VagaVitrinePcD"))}</li>` : ""}</ul>`
        : "";

    // Wrapper vazio ocuparia a coluna dele e desalinharia a linha no desktop.
    const metaHtml = (chipsHtml || salario || quando)
        ? `<div class="cartao-vaga-meta">${chipsHtml}`
          + `${salario ? `<span class="cartao-vaga-salario">${escaparHtml(salario)}</span>` : ""}`
          + `${quando ? `<span class="cartao-vaga-quando">${escaparHtml(quando)}</span>` : ""}</div>`
        : "";

    return `<article class="cartao-vaga">`
        + `<div class="cartao-vaga-principal">`
        + `<${nivelTitulo} class="cartao-vaga-titulo">`
        + `<a href="${escaparHtml(urlBase)}/${escaparHtml(vaga.url)}" class="stretched-link"`
        + `${urlBase ? ' target="_blank" rel="noopener"' : ""}>${escaparHtml(vaga.titulo)}</a>`
        + `</${nivelTitulo}>`
        + `${cidades ? `<p class="cartao-vaga-local"><i class="bi bi-geo-alt" aria-hidden="true"></i><span>${escaparHtml(cidades)}</span></p>` : ""}`
        + `</div>${metaHtml}`
        + `<i class="bi bi-chevron-right cartao-vaga-seta" aria-hidden="true"></i>`
        + `</article>`;
}

/**
 * Esqueleto de carregamento no formato da LINHA da vitrine. O esqueleto do jobboard tem logo e 6em
 * de chamada: no lugar de linhas de ~56px ele daria um salto de layout de várias telas quando os
 * cartões de verdade chegassem.
 */
export function esqueletoCartaoVitrine(quantidade) {
    let html = "";
    for (let i = 0; i < quantidade; ++i) {
        html += `
        <div class="cartao-vaga card-loading">
            <div class="cartao-vaga-principal">
                <div class="card-loading-bar" style="width: 45%"></div>
                <div class="card-loading-bar" style="width: 30%"></div>
            </div>
        </div>
        `;
    }
    return html;
}
